import React, { useState, useEffect, useCallback } from 'react';
import API_URL from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../utils/apiFetch';

interface Customer { id: string | number; name: string; }
interface CreditTerm {
  id: string; name: string; duration: number;
  appliesTo: 'Customer' | 'Supplier' | 'Both';
  description?: string; active?: boolean;
  category?: string; customerIds?: (string | number)[];
}

const GREEN = '#10b981';
const quickTemplates = [
  { name: 'Cash on Delivery', duration: 0,  appliesTo: 'Customer' as const, description: 'Payment due immediately upon delivery' },
  { name: 'Net 7 Days',       duration: 7,  appliesTo: 'Customer' as const, description: 'Payment due within 7 days' },
  { name: 'Net 15 Days',      duration: 15, appliesTo: 'Customer' as const, description: 'Payment due within 15 days' },
  { name: 'Net 30 Days',      duration: 30, appliesTo: 'Both'     as const, description: 'Standard 30-day payment terms' },
  { name: 'Net 45 Days',      duration: 45, appliesTo: 'Both'     as const, description: 'Extended 45-day payment terms' },
  { name: 'Net 60 Days',      duration: 60, appliesTo: 'Supplier' as const, description: '60-day terms for suppliers' },
  { name: 'Net 90 Days',      duration: 90, appliesTo: 'Supplier' as const, description: '90-day extended payment terms' },
];

function getCat(d: number) {
  if (d === 0) return { label: 'Immediate', color: '#ef4444' };
  if (d <= 15)  return { label: 'Short',    color: '#f59e0b' };
  if (d <= 45)  return { label: 'Standard', color: '#2563eb' };
  return              { label: 'Extended',  color: '#10b981' };
}

function appliesToStyle(a: string) {
  if (a === 'Customer') return { bg: '#eff6ff', color: '#1d4ed8' };
  if (a === 'Supplier') return { bg: '#f0fdf4', color: '#15803d' };
  return { bg: '#faf5ff', color: '#7c3aed' };
}

const EMPTY_FORM = { name: '', duration: '30', appliesTo: 'Both' as 'Customer' | 'Supplier' | 'Both', description: '', customerIds: [] as string[] };

const CreditTerms: React.FC = () => {
  const { token } = useAuth();
  const [terms, setTerms]         = useState<CreditTerm[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [editing, setEditing]     = useState<CreditTerm | null>(null);
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState<{ msg: string; type: 'success'|'error' } | null>(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState<CreditTerm | null>(null);

  const authH = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const showToast = (msg: string, type: 'success'|'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchTerms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/credit-terms`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setTerms(await res.json());
    } catch { showToast('Failed to load credit terms', 'error'); }
    finally { setLoading(false); }
  }, [token]);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_URL}/customers`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setCustomers((Array.isArray(data) ? data : []).map((c: any) => ({ id: String(c.id || c._id), name: c.name })));
      }
    } catch {}
  }, [token]);

  useEffect(() => { fetchTerms(); fetchCustomers(); }, [fetchTerms, fetchCustomers]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setModal(true); };
  const openEdit = (t: CreditTerm) => {
    setEditing(t);
    setForm({ name: t.name, duration: String(t.duration), appliesTo: t.appliesTo, description: t.description || '', customerIds: (t.customerIds || []).map(String) });
    setModal(true);
  };
  const closeModal = () => { setModal(false); setEditing(null); setForm(EMPTY_FORM); };

  const handleSave = async () => {
    if (!form.name.trim()) { showToast('Name is required', 'error'); return; }
    setSaving(true);
    try {
      const dur = Number(form.duration) || 0;
      const cat = getCat(dur).label.toLowerCase();
      const payload = { name: form.name.trim(), duration: dur, appliesTo: form.appliesTo, description: form.description, category: cat, active: true, customerIds: form.customerIds };
      const url = editing ? `${API_URL}/credit-terms/${editing.id}` : `${API_URL}/credit-terms`;
      const res = await apiFetch(url, { method: editing ? 'PUT' : 'POST', headers: authH, body: JSON.stringify(payload) });
      if (res.ok) { showToast(editing ? 'Updated' : 'Created'); closeModal(); fetchTerms(); }
      else showToast('Failed to save', 'error');
    } catch { showToast('Failed to save', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (t: CreditTerm) => {
    try {
      const res = await apiFetch(`${API_URL}/credit-terms/${t.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { showToast('Deleted'); fetchTerms(); }
      else showToast('Failed to delete', 'error');
    } catch { showToast('Failed to delete', 'error'); }
    setDeleteConfirm(null);
  };

  const handleQuickAdd = async (tpl: typeof quickTemplates[0]) => {
    if (terms.some(t => t.name === tpl.name)) { showToast(`"${tpl.name}" already added`); return; }
    try {
      const dur = tpl.duration; const cat = getCat(dur).label.toLowerCase();
      const res = await apiFetch(`${API_URL}/credit-terms`, { method: 'POST', headers: authH, body: JSON.stringify({ ...tpl, active: true, category: cat, customerIds: [] }) });
      if (res.ok) { showToast(`${tpl.name} added`); fetchTerms(); }
      else showToast('Failed to add', 'error');
    } catch { showToast('Failed to add', 'error'); }
  };

  const customerMap = Object.fromEntries(customers.map(c => [String(c.id), c.name]));

  return (
    <div style={s.page}>
      {toast && (
        <div style={{ ...s.toast, background: toast.type === 'success' ? '#10b981' : '#ef4444' }}>
          {toast.type === 'success' ? '✓' : '✕'} {toast.msg}
        </div>
      )}

      <div style={s.header}>
        <div>
          <h1 style={s.title}>💳 Credit Terms</h1>
          <p style={s.sub}>Define payment windows for customers and suppliers</p>
        </div>
        <button style={s.primaryBtn} onClick={openCreate}>+ Create Custom Term</button>
      </div>

      <div style={s.infoBanner}>
        <span style={{ fontSize: 18 }}>ℹ️</span>
        <div>
          <strong style={{ color: '#1d4ed8' }}>What are Credit Terms?</strong>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#4b5563' }}>
            Credit terms set how many days a customer has to pay — e.g. <strong>Net 30</strong> means payment due within 30 days. Assign terms to customers so the agreed window always appears on invoices.
          </p>
        </div>
      </div>

      <div style={s.section}>
        <h2 style={s.sectionTitle}>⚡ Quick Add Common Terms</h2>
        <div style={s.quickGrid}>
          {quickTemplates.map((tpl, i) => {
            const added = terms.some(t => t.name === tpl.name);
            const cat = getCat(tpl.duration);
            return (
              <button key={i} style={{ ...s.quickCard, opacity: added ? 0.5 : 1, cursor: added ? 'default' : 'pointer', borderColor: cat.color + '44' }}
                onClick={() => !added && handleQuickAdd(tpl)} disabled={added}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: '#111827' }}>{tpl.name}</div>
                <span style={{ ...s.badge, background: cat.color + '18', color: cat.color }}>
                  {tpl.duration === 0 ? 'Immediate' : `${tpl.duration} days`}
                </span>
                {added && <div style={{ fontSize: 11, color: GREEN, marginTop: 6, fontWeight: 600 }}>✓ Added</div>}
              </button>
            );
          })}
        </div>
      </div>

      <div style={s.section}>
        <h2 style={s.sectionTitle}>📋 Your Credit Terms ({terms.length})</h2>
        {loading ? (
          <div style={s.emptyState}>Loading...</div>
        ) : terms.length === 0 ? (
          <div style={s.emptyState}>No credit terms yet. Use Quick Add above or create a custom term.</div>
        ) : (
          <div style={s.termsGrid}>
            {terms.map(term => {
              const cat = getCat(term.duration);
              const ats = appliesToStyle(term.appliesTo);
              const assigned = (term.customerIds || []).map(id => customerMap[String(id)]).filter(Boolean);
              return (
                <div key={term.id} style={{ ...s.termCard, borderLeftColor: cat.color }}>
                  <div style={s.termCardTop}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', flex: 1 }}>{term.name}</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={s.iconBtn} onClick={() => openEdit(term)} title="Edit">✏️</button>
                      <button style={{ ...s.iconBtn, color: '#ef4444' }} onClick={() => setDeleteConfirm(term)} title="Delete">🗑️</button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' as const }}>
                    <span style={{ ...s.badge, background: cat.color + '18', color: cat.color }}>
                      {term.duration === 0 ? 'Immediate' : `${term.duration} days`}
                    </span>
                    <span style={{ ...s.badge, background: ats.bg, color: ats.color }}>{term.appliesTo}</span>
                  </div>
                  {term.description && <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>{term.description}</p>}
                  {assigned.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 4, marginTop: 6 }}>
                      {assigned.map(n => <span key={n} style={{ ...s.badge, background: '#f3f4f6', color: '#374151' }}>👤 {n}</span>)}
                    </div>
                  ) : (
                    <p style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>No customers assigned</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{editing ? '✏️ Edit Term' : '➕ Create Credit Term'}</h2>
              <button style={s.closeBtn} onClick={closeModal}>✕</button>
            </div>
            <div style={s.modalBody}>
              <label style={s.label}>Term Name *</label>
              <input style={s.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Net 30 Days" />

              <label style={s.label}>Payment Period (Days)</label>
              <input style={s.input} type="number" min={0} value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} placeholder="30" />
              <p style={{ fontSize: 12, color: '#6b7280', margin: '-8px 0 16px' }}>Enter 0 for immediate / cash on delivery</p>

              <label style={s.label}>Applies To</label>
              <select style={s.input} value={form.appliesTo} onChange={e => setForm(f => ({ ...f, appliesTo: e.target.value as any }))}>
                <option value="Customer">Customers — money coming in</option>
                <option value="Supplier">Suppliers — money going out</option>
                <option value="Both">Both</option>
              </select>

              <label style={s.label}>Assign to Customers (optional)</label>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, maxHeight: 160, overflowY: 'auto' as const, padding: '8px 12px', background: 'white' }}>
                {customers.length === 0
                  ? <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>No customers found</p>
                  : customers.map(c => (
                    <label key={String(c.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: 13 }}>
                      <input type="checkbox" checked={form.customerIds.includes(String(c.id))}
                        onChange={e => setForm(f => ({ ...f, customerIds: e.target.checked ? [...f.customerIds, String(c.id)] : f.customerIds.filter(id => id !== String(c.id)) }))} />
                      {c.name}
                    </label>
                  ))}
              </div>

              <label style={{ ...s.label, marginTop: 16 }}>Description (optional)</label>
              <textarea style={{ ...s.input, height: 70, resize: 'vertical' as const }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Additional details..." />
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={closeModal}>Cancel</button>
              <button style={s.primaryBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editing ? 'Save Changes' : 'Create Term'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, maxWidth: 400 }}>
            <div style={s.modalHeader}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#ef4444' }}>🗑️ Delete Term</h2>
              <button style={s.closeBtn} onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <p style={{ color: '#374151', margin: 0 }}>Delete <strong>"{deleteConfirm.name}"</strong>? Customers already assigned will keep their invoices — only this term definition is removed.</p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button style={{ ...s.primaryBtn, background: '#ef4444' }} onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s: any = {
  page: { maxWidth: 1200, margin: '0 auto', padding: '32px 24px 60px', background: '#f8fafc', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" },
  toast: { position: 'fixed', top: 20, right: 20, color: 'white', padding: '12px 20px', borderRadius: 10, fontWeight: 600, fontSize: 14, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 16 },
  title: { margin: 0, fontSize: 24, fontWeight: 700, color: '#111827', letterSpacing: '-0.5px' },
  sub: { margin: '4px 0 0', fontSize: 13, color: '#6b7280' },
  infoBanner: { display: 'flex', gap: 12, alignItems: 'flex-start', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '14px 18px', marginBottom: 28 },
  section: { background: 'white', borderRadius: 12, padding: '20px 24px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#111827' },
  quickGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 },
  quickCard: { background: 'white', border: '1.5px solid', borderRadius: 10, padding: '12px 14px', textAlign: 'center', transition: 'box-shadow 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
  termsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 },
  termCard: { background: '#fafafa', border: '1px solid #e5e7eb', borderLeft: '4px solid', borderRadius: 10, padding: '16px 18px' },
  termCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: '2px 4px', borderRadius: 4 },
  emptyState: { textAlign: 'center', color: '#9ca3af', padding: '40px 0', fontSize: 14 },
  primaryBtn: { background: '#10b981', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  cancelBtn: { background: 'white', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { background: 'white', borderRadius: 14, width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 16px', borderBottom: '1px solid #f3f4f6' },
  modalBody: { padding: '20px 24px' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid #f3f4f6' },
  closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280', padding: 4 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 },
  input: { width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 14, marginBottom: 16, boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none', background: 'white', color: '#111827' },
};

export default CreditTerms;
